import type { Query, QueryClient } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import type {
  ProposalItem,
  TradeProposal,
} from "modules/trade-proposals/model/tradeProposal";
import { tradeItemGroupKey } from "modules/tradeLists/utils/tradeItemGroupKey";
import type {
  TradeItemAvailability,
  TradeItemAvailabilityResponse,
} from "modules/tradeLists/model/tradeList";

const BINDER_ITEM_QUERY_PREFIXES = new Set<string>([
  QUERIES.TRADE_LIST_ITEMS,
  QUERIES.COLLECTION_CARDS,
]);

const BINDER_FACET_QUERY_PREFIXES = new Set<string>([
  QUERIES.TRADE_LIST_FACETS,
  QUERIES.COLLECTION_FACETS,
]);

function binderTradeMembershipRequest(query: Query):
  | {
      facets?: { inTrade?: unknown };
    }
  | undefined {
  const request = query.queryKey[1];
  if (!request || typeof request !== "object" || Array.isArray(request)) {
    return undefined;
  }

  const { binder, section, facets } = request as {
    binder?: unknown;
    section?: unknown;
    facets?: { inTrade?: unknown };
  };
  return binder === true && (section === "haves" || section === "collection")
    ? { facets }
    : undefined;
}

function isBinderTradeMembershipFacetQuery(query: Query): boolean {
  return (
    BINDER_FACET_QUERY_PREFIXES.has(String(query.queryKey[0])) &&
    binderTradeMembershipRequest(query) !== undefined
  );
}

function isInTradeBinderItemQuery(query: Query): boolean {
  return (
    BINDER_ITEM_QUERY_PREFIXES.has(String(query.queryKey[0])) &&
    binderTradeMembershipRequest(query)?.facets?.inTrade === true
  );
}

/**
 * Reservation changes always alter the visible In trade facet count, but
 * alter grid membership only while that facet is selected.
 */
export function invalidateBinderTradeMembershipQueries(
  queryClient: QueryClient
): void {
  void queryClient.invalidateQueries({
    predicate: isBinderTradeMembershipFacetQuery,
    refetchType: "active",
  });
  void queryClient.invalidateQueries({
    predicate: isInTradeBinderItemQuery,
    refetchType: "active",
  });
}

function isActive(proposal: TradeProposal | null): proposal is TradeProposal {
  return (
    !!proposal &&
    !["declined", "withdrawn", "completed"].includes(proposal.status)
  );
}

function proposalReservations(proposal: TradeProposal | null) {
  const reservations = new Map<string, number>();
  if (!isActive(proposal)) return reservations;

  const addSide = (items: ProposalItem[]) => {
    for (const item of items) {
      if (!item.ids?.length) continue;
      const key = tradeItemGroupKey(item.ids);
      reservations.set(key, (reservations.get(key) ?? 0) + item.quantity);
    }
  };

  addSide(proposal.userSide);
  addSide(proposal.partnerSide);
  return reservations;
}

function patchAvailabilityItem(
  item: TradeItemAvailability,
  previousReservations: Map<string, number>,
  nextReservations: Map<string, number>
): TradeItemAvailability {
  const key = tradeItemGroupKey(item.ids);
  const previousQuantity = previousReservations.get(key) ?? 0;
  const nextQuantity = nextReservations.get(key) ?? 0;

  if (previousQuantity === nextQuantity) return item;

  return {
    ...item,
    // The optimistic event carries quantities, not the concrete IDs chosen by
    // the transaction. Drop the exact subset until the compact endpoint
    // refetches rather than leaving a stale copy actionable.
    availableIds: undefined,
    spokenForQuantity:
      Math.max(0, item.spokenForQuantity - previousQuantity) + nextQuantity,
  };
}

/** Update only known ID groups, then reconcile against the live endpoint. */
export function syncProposalAvailabilityInCache(
  queryClient: QueryClient,
  previousProposal: TradeProposal | null,
  nextProposal: TradeProposal
) {
  const previousReservations = proposalReservations(previousProposal);
  const nextReservations = proposalReservations(nextProposal);

  queryClient.setQueriesData<TradeItemAvailabilityResponse>(
    { queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY] },
    (data) =>
      data
        ? {
            ...data,
            items: data.items.map((item) =>
              patchAvailabilityItem(
                item,
                previousReservations,
                nextReservations
              )
            ),
          }
        : data
  );

  void queryClient.invalidateQueries({
    queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY],
    refetchType: "active",
  });

  invalidateBinderTradeMembershipQueries(queryClient);
}

/** A conflict means the overlay was stale; only refetch this compact endpoint. */
export async function refreshProposalAvailabilityAfterConflict(
  queryClient: QueryClient
): Promise<void> {
  queryClient.removeQueries({
    queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY],
    type: "inactive",
  });
  await queryClient.invalidateQueries({
    queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY],
    refetchType: "active",
  });
}
